import { useState } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/**
 * Shared topic-space presentational primitives, matching the approved prototype:
 * the two lens pills (blind = solid indigo, field = indigo tint), the banded
 * health/score pill (green ≥70 / amber 50–69 / red <50), and the rank-movement
 * glyph. No invented composite health metric — HealthPill renders whatever value
 * it's handed (the topic's field sentiment) in the prototype's banded slot.
 */

/**
 * Client-side "show more" for a capped reporting list: the visible slice, the
 * expanded flag, a toggle, and how many rows are hidden while collapsed. Payloads
 * are unchanged — this only limits what renders.
 */
export function useShowMore<T>(items: T[], limit: number) {
  const [expanded, setExpanded] = useState(false);
  return {
    visible: expanded ? items : items.slice(0, limit),
    expanded,
    toggle: () => setExpanded((v) => !v),
    hiddenCount: Math.max(0, items.length - limit),
  };
}

/** Full-width, muted ghost toggle for the capped lists: "Show all {N}" / "Show fewer". */
export function ShowMoreButton({ expanded, count, noun, onClick, className }: {
  expanded: boolean;
  count: number;
  noun?: string;
  onClick: () => void;
  className?: string;
}) {
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={onClick}
      className={cn("w-full text-slate-500 hover:text-brand-600", className)}
    >
      {expanded ? "Show fewer" : `Show all ${count}${noun ? ` ${noun}` : ""}`}
    </Button>
  );
}

/**
 * Collapse a list to `limit` rows while always keeping the pinned (primary) row
 * visible. When the pinned row sits beyond the cap, returns the top `limit - 1`
 * rows (`head`), the count of rows skipped before it (`gap`), and the pinned row
 * itself so callers can render a muted "… {gap} more" marker between them.
 * Otherwise `pinned` is null and `head` is the plain top-`limit` slice.
 */
export function pinnedCollapse<T>(
  items: T[],
  limit: number,
  isPinned: (item: T) => boolean,
): { head: T[]; gap: number; pinned: T | null } {
  const idx = items.findIndex(isPinned);
  if (idx === -1 || idx < limit) return { head: items.slice(0, limit), gap: 0, pinned: null };
  const head = items.slice(0, limit - 1);
  return { head, gap: idx - head.length, pinned: items[idx]! };
}

/** BLIND / FIELD lens pill. Solid indigo for blind (it leads), indigo tint for field. */
export function LensPill({ lens, className }: { lens: "blind" | "field"; className?: string }) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded font-bold uppercase tabular-nums",
        "text-[10px] tracking-[0.06em] px-[7px] py-[3px] leading-none",
        lens === "blind" ? "bg-brand-600 text-white" : "bg-brand-50 text-brand-600",
        className,
      )}
    >
      {lens}
    </span>
  );
}

/** Sentiment band for a 0–100 value: ≥70 good / 50–69 mid / <50 low. */
export function sentimentBand(value: number | null | undefined): "good" | "mid" | "low" | "pending" {
  if (value === null || value === undefined || Number.isNaN(value)) return "pending";
  if (value >= 70) return "good";
  if (value >= 50) return "mid";
  return "low";
}

/**
 * Banded pill for a topic's field-sentiment value, in the prototype's score-pill
 * slot (rounded-full, bold, tabular). Renders "—" (dashed, muted) when pending.
 */
export function HealthPill({ value, className }: { value: number | null | undefined; className?: string }) {
  const band = sentimentBand(value);
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2.5 py-0.5 text-[13px] font-bold tabular-nums leading-none",
        band === "good" && "bg-green-50 text-green-600",
        band === "mid" && "bg-accent-50 text-accent-700",
        band === "low" && "bg-red-50 text-red-600",
        band === "pending" && "border border-dashed border-slate-300 bg-slate-50 text-slate-400",
        className,
      )}
    >
      {band === "pending" ? "—" : Math.round(value as number)}
    </span>
  );
}

/**
 * Rank movement vs a prior rank (lower rank number = better): ▲n up / ▼n down /
 * — flat / "new" (no prior). null when the current rank is unknown.
 */
export function rankMovement(rank: number | null, prevRank: number | null): { glyph: string; cls: string } | null {
  if (rank === null) return null;
  if (prevRank === null) return { glyph: "new", cls: "text-slate-400" };
  const diff = prevRank - rank;
  if (diff > 0) return { glyph: `▲${diff}`, cls: "text-green-600" };
  if (diff < 0) return { glyph: `▼${-diff}`, cls: "text-red-500" };
  return { glyph: "—", cls: "text-slate-400" };
}

/** Rendered rank-movement glyph (tabular, semibold, colored by direction). */
export function MovementGlyph({ rank, prevRank, className }: { rank: number | null; prevRank: number | null; className?: string }) {
  const mv = rankMovement(rank, prevRank);
  if (!mv) return null;
  return <span className={cn("text-[11px] font-semibold tabular-nums", mv.cls, className)}>{mv.glyph}</span>;
}

/** Direction band for a sentiment delta: ±0.05 is the flat threshold (matches
 *  the snapshot's MoM convention). "none" when the delta is missing. */
export function deltaDirection(delta: number | null | undefined): "up" | "down" | "flat" | "none" {
  if (delta === null || delta === undefined || Number.isNaN(delta)) return "none";
  if (delta > 0.05) return "up";
  if (delta < -0.05) return "down";
  return "flat";
}

/**
 * A sentiment delta, colored + tabular. `glyph` shows the prototype's ▲/▼
 * triangles (headers/stats); off gives a bare signed number (compact rows).
 * Returns null when the delta is absent. `suffix` appends a label (" WoW").
 */
export function SentimentDelta({ delta, suffix = "", glyph = true, digits = 1, className }: {
  delta: number | null | undefined;
  suffix?: string;
  glyph?: boolean;
  digits?: number;
  className?: string;
}) {
  const dir = deltaDirection(delta);
  if (dir === "none") return null;
  const d = delta as number;
  const prefix = glyph
    ? (dir === "up" ? "▲ +" : dir === "down" ? "▼ " : "")
    : (d > 0 ? "+" : "");
  return (
    <span
      className={cn(
        "font-semibold tabular-nums",
        dir === "up" && "text-green-600",
        dir === "down" && "text-red-500",
        dir === "flat" && "text-slate-400",
        className,
      )}
    >
      {prefix}{d.toFixed(digits)}{suffix}
    </span>
  );
}
